import { useMemo } from "react";

import { useRoleContext } from "./useRoleContext";
import { useSwrFetch } from "./useSwrFetch";

interface UseShipmentProps {
  shipmentId: string | number | undefined;
}

interface UseShipmentResult<T> {
  shipment: T | undefined;
  loading: boolean;
  error: string | null;
  refetchShipment: () => void;
}

export function useShipment<
  T = CarrierCondensedShipment | ShipperCondensedShipment,
>(props: UseShipmentProps): UseShipmentResult<T> {
  const { shipmentId } = props;
  const { role } = useRoleContext();

  const url = useMemo(() => {
    if (role && shipmentId) {
      // Convert number to string for URL
      const shipmentIdStr =
        typeof shipmentId === "number" ? shipmentId.toString() : shipmentId;

      if (role.type === "SHIPPER") {
        return `/shippers/${role.id}/shipments/${shipmentIdStr}/condensed`;
      }
      if (role.type === "CARRIER") {
        return `/shipments/${shipmentIdStr}/condensed`;
      }
    }
    return null;
  }, [role, shipmentId]);

  const { data, loading, error, refetch } = useSwrFetch<T>({
    url,
    errorMessage: "Failed to fetch shipment details",
  });

  return {
    shipment: data,
    loading,
    error,
    refetchShipment: refetch,
  };
}
